import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DatePickerFr } from '../components/ui/DatePickerFr';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ChangePasswordForm from '../components/ProfileEdit/ChangePasswordForm';
import MesDonneesPanel from '../components/MesDonneesPanel';
import CooperDocumentsPanel from '../components/ProfileEdit/CooperDocumentsPanel';
import { Mail, Phone, MapPin, Calendar, Building2, HardHat, Globe, Bell, Users } from 'lucide-react';
import UnifiedActivitySelector from '../components/UnifiedActivitySelector';
import CountrySelect from '../components/ui/CountrySelect';
import { formatCodeNAF } from '../utils/codeNaf';
import SiretInput from '../components/ui/SiretInput';
import { INDICATIFS_TELEPHONIQUES, STATUTS_JURIDIQUES, getFlagUrl } from '../config/membreFormConfig';
import { toast } from 'sonner';

export default function ProfileTabsContent({
  profile, userType, editMode, formData, handleChange,
  phoneData, setPhoneData, selectedIndicatif, phoneValidation, getAuthHeader,
}) {
  return (
    <>
      {/* Tab Informations personnelles */}
      <TabsContent value="informations" className="mt-0 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Nom */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Nom</Label>
            {editMode ? (
              <Input
                value={formData.nom}
                onChange={(e) => handleChange('nom', e.target.value)}
                className="h-11"
              />
            ) : (
              <p className="h-11 px-3 py-2 bg-slate-50 rounded-lg flex items-center">{formData.nom || '-'}</p>
            )}
          </div>

          {/* Prénom */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Prénom</Label>
            {editMode ? (
              <Input
                value={formData.prenom}
                onChange={(e) => handleChange('prenom', e.target.value)}
                className="h-11"
              />
            ) : (
              <p className="h-11 px-3 py-2 bg-slate-50 rounded-lg flex items-center">{formData.prenom || '-'}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email
            </Label>
            {editMode ? (
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="h-11"
              />
            ) : (
              <p className="h-11 px-3 py-2 bg-slate-50 rounded-lg flex items-center">{formData.email || '-'}</p>
            )}
          </div>

          {/* Téléphone avec drapeau */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Téléphone
            </Label>
            {editMode ? (
              <div className="flex gap-2">
                <Select
                  value={phoneData.indicatif}
                  onValueChange={(val) => setPhoneData(prev => ({ ...prev, indicatif: val }))}
                >
                  <SelectTrigger className="w-[130px] h-11">
                    <SelectValue>
                      <span className="flex items-center gap-2">
                        <img
                          src={getFlagUrl(selectedIndicatif.flag)}
                          alt={selectedIndicatif.name}
                          className="w-5 h-4 object-cover rounded-sm"
                        />
                        {selectedIndicatif.code}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-[250px]">
                    {INDICATIFS_TELEPHONIQUES.map((ind, idx) => (
                      <SelectItem key={`${ind.code}-${idx}`} value={ind.code}>
                        <span className="flex items-center gap-2">
                          <img src={getFlagUrl(ind.flag)} alt={ind.name} className="w-5 h-4 object-cover rounded-sm" />
                          {ind.code} {ind.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex-1 relative">
                  <Input
                    value={phoneData.numero}
                    onChange={(e) => setPhoneData(prev => ({ ...prev, numero: e.target.value.replace(/[^\d\s]/g, '') }))}
                    className={`h-11 ${phoneValidation.isValid && phoneData.numero ? 'border-green-500' : ''}`}
                    placeholder={`${phoneValidation.expectedLength} chiffres`}
                  />
                  {phoneData.numero && (
                    <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${phoneValidation.isValid ? 'text-green-600' : 'text-orange-500'}`}>
                      {phoneValidation.currentLength}/{phoneValidation.expectedLength}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="h-11 px-3 py-2 bg-slate-50 rounded-lg flex items-center gap-2">
                <img
                  src={getFlagUrl(selectedIndicatif.flag)}
                  alt={selectedIndicatif.name}
                  className="w-5 h-4 object-cover rounded-sm"
                />
                <span className="text-slate-600 font-medium">{selectedIndicatif.code}</span>
                <span className="text-slate-400">|</span>
                <span>{phoneData.numero || '-'}</span>
              </div>
            )}
          </div>

          {/* Adresse */}
          <div className="space-y-2 md:col-span-2">
            <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Adresse
            </Label>
            {editMode ? (
              <Input
                value={formData.adresse}
                onChange={(e) => handleChange('adresse', e.target.value)}
                className="h-11"
              />
            ) : (
              <p className="h-11 px-3 py-2 bg-slate-50 rounded-lg flex items-center">{formData.adresse || '-'}</p>
            )}
          </div>

          {/* Pays */}
          <div className="space-y-2">
            {editMode ? (
              <CountrySelect
                value={formData.pays}
                onChange={(val) => handleChange('pays', val)}
                label="Pays"
                showLabel={true}
              />
            ) : (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Pays
                </Label>
                <p className="h-11 px-3 py-2 bg-slate-50 rounded-lg flex items-center">{formData.pays || '-'}</p>
              </div>
            )}
          </div>

          {/* Date de naissance */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Date de naissance
            </Label>
            {editMode ? (
              <DatePickerFr
                value={formData.date_naissance}
                onChange={(v) => handleChange('date_naissance', v)}
                className="h-11 w-full"
              />
            ) : (
              <p className="h-11 px-3 py-2 bg-slate-50 rounded-lg flex items-center">
                {formData.date_naissance ? new Date(formData.date_naissance).toLocaleDateString('fr-FR') : '-'}
              </p>
            )}
          </div>

          {/* Genre */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Genre</Label>
            {editMode ? (
              <Select value={formData.genre} onValueChange={(val) => handleChange('genre', val)}>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="homme">Homme</SelectItem>
                  <SelectItem value="femme">Femme</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <p className="h-11 px-3 py-2 bg-slate-50 rounded-lg flex items-center capitalize">{formData.genre || '-'}</p>
            )}
          </div>
        </div>
      </TabsContent>

      {/* Tab Entreprise */}
      <TabsContent value="entreprise" className="mt-0 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Raison sociale */}
          <div className="space-y-2 md:col-span-2">
            <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Raison sociale
            </Label>
            {editMode ? (
              <Input
                value={formData.raison_sociale}
                onChange={(e) => handleChange('raison_sociale', e.target.value)}
                className="h-11"
              />
            ) : (
              <p className="h-11 px-3 py-2 bg-slate-50 rounded-lg flex items-center">{formData.raison_sociale || '-'}</p>
            )}
          </div>

          {/* SIRET */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">N° SIRET</Label>
            {editMode ? (
              <SiretInput
                value={formData.siret}
                onChange={(val) => handleChange('siret', val)}
                showLabel={false}
                onAutoComplete={(data) => {
                  if (data.raison_sociale) handleChange('raison_sociale', data.raison_sociale);
                  if (data.code_naf) handleChange('code_naf', formatCodeNAF(data.code_naf));
                  if (data.forme_juridique) handleChange('statut_juridique', data.forme_juridique);
                  if (data.adresse) handleChange('adresse', data.adresse);
                  if (data.code_postal) handleChange('code_postal', data.code_postal);
                  if (data.ville) handleChange('ville', data.ville);
                }}
              />
            ) : (
              <p className="h-11 px-3 py-2 bg-slate-50 rounded-lg flex items-center font-mono">{formData.siret || '-'}</p>
            )}
          </div>

          {/* Code NAF */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Code NAF</Label>
            {editMode ? (
              <Input
                value={formData.code_naf}
                onChange={(e) => handleChange('code_naf', e.target.value.toUpperCase())}
                onBlur={(e) => handleChange('code_naf', formatCodeNAF(e.target.value))}
                className="h-11 font-mono"
                placeholder="Ex: 43.99C"
                maxLength={6}
              />
            ) : (
              <p className="h-11 px-3 py-2 bg-slate-50 rounded-lg flex items-center font-mono">{formatCodeNAF(formData.code_naf) || '-'}</p>
            )}
          </div>

          {/* Statut juridique */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Statut juridique</Label>
            {editMode ? (
              <Select value={formData.statut_juridique} onValueChange={(val) => handleChange('statut_juridique', val)}>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {STATUTS_JURIDIQUES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="h-11 px-3 py-2 bg-slate-50 rounded-lg flex items-center">{formData.statut_juridique || '-'}</p>
            )}
          </div>

          {/* Effectif */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <Users className="h-4 w-4" />
              Effectif
            </Label>
            {editMode ? (
              <Select value={formData.effectif} onValueChange={(val) => handleChange('effectif', val)}>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 personne</SelectItem>
                  <SelectItem value="2-5">2-5 personnes</SelectItem>
                  <SelectItem value="6-10">6-10 personnes</SelectItem>
                  <SelectItem value="11-50">11-50 personnes</SelectItem>
                  <SelectItem value="50+">Plus de 50</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <p className="h-11 px-3 py-2 bg-slate-50 rounded-lg flex items-center">{formData.effectif || '-'}</p>
            )}
          </div>
        </div>
      </TabsContent>

      {/* Tab Activité Professionnelle */}
      <TabsContent value="activite" className="mt-0 space-y-6">
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-200">
          <h3 className="font-semibold text-orange-800 flex items-center gap-2 mb-4">
            <HardHat className="h-5 w-5" />
            Catégorie Professionnelle BTP
          </h3>
          {editMode ? (
            <UnifiedActivitySelector
              value={formData.activite_professionnelle}
              onChange={(val) => handleChange('activite_professionnelle', val)}
            />
          ) : (
            <div className="space-y-2">
              {formData.activite_professionnelle ? (
                <>
                  <p><strong>Secteur :</strong> {formData.activite_professionnelle.secteur}</p>
                  <p><strong>Catégorie :</strong> {formData.activite_professionnelle.categorie}</p>
                  {formData.activite_professionnelle.metiers?.length > 0 && (
                    <div>
                      <strong>Métiers :</strong>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.activite_professionnelle.metiers.map((m, i) => (
                          <Badge key={i} variant="secondary">{m}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-slate-500 italic">Aucune activité professionnelle renseignée</p>
              )}
            </div>
          )}
        </div>
      </TabsContent>

      {/* Tab Expertise (pour experts) */}
      <TabsContent value="expertise" className="mt-0 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Spécialité</Label>
            {editMode ? (
              <Input
                value={formData.specialite}
                onChange={(e) => handleChange('specialite', e.target.value)}
                className="h-11"
              />
            ) : (
              <p className="h-11 px-3 py-2 bg-slate-50 rounded-lg flex items-center">{formData.specialite || '-'}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Années d&apos;expérience</Label>
            {editMode ? (
              <Input
                type="number"
                value={formData.experience_annees}
                onChange={(e) => handleChange('experience_annees', e.target.value)}
                className="h-11"
              />
            ) : (
              <p className="h-11 px-3 py-2 bg-slate-50 rounded-lg flex items-center">{formData.experience_annees || '-'} ans</p>
            )}
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="text-sm font-medium text-slate-700">Certifications</Label>
            {editMode ? (
              <Input
                value={formData.certifications}
                onChange={(e) => handleChange('certifications', e.target.value)}
                className="h-11"
                placeholder="Ex: ISO 9001, QUALIBAT..."
              />
            ) : (
              <p className="h-11 px-3 py-2 bg-slate-50 rounded-lg flex items-center">{formData.certifications || '-'}</p>
            )}
          </div>
        </div>
      </TabsContent>

      {/* Tab Sécurité */}
      <TabsContent value="securite" className="mt-0 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ChangePasswordForm
            getAuthHeader={getAuthHeader}
            onSuccess={() => toast.success('Mot de passe mis à jour !')}
          />

          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Bell className="h-5 w-5 text-slate-600" />
                Notifications
              </CardTitle>
              <CardDescription>Gérez vos préférences de notifications</CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full">
                Paramètres notifications
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Informations de compte */}
        <Card className="border-slate-200 bg-slate-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Informations du compte</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">ID utilisateur</span>
              <span className="font-mono text-slate-700">{profile?.id?.slice(0, 8)}...</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Date d&apos;inscription</span>
              <span className="text-slate-700">
                {profile?.date_inscription
                  ? new Date(profile.date_inscription).toLocaleDateString('fr-FR')
                  : '-'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Dernière connexion</span>
              <span className="text-slate-700">
                {profile?.derniere_connexion
                  ? new Date(profile.derniere_connexion).toLocaleString('fr-FR')
                  : '-'}
              </span>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Tab Mes données (RGPD universel - Article 15/17/20) */}
      <TabsContent value="mes-donnees" className="mt-0">
        <MesDonneesPanel />
      </TabsContent>

      {/* Tab Mes documents (uniquement COOPER'S) */}
      {userType === 'cooper' && (
        <TabsContent value="mes-documents" className="mt-0">
          <CooperDocumentsPanel />
        </TabsContent>
      )}
    </>
  );
}
